import React, { useEffect, useMemo, useState } from 'react';
import { DatePicker } from 'antd';
import dayjs from 'dayjs';
import weekday from 'dayjs/plugin/weekday';
import localeData from 'dayjs/plugin/localeData';
import weekOfYear from 'dayjs/plugin/weekOfYear';

dayjs.extend(weekday);
dayjs.extend(localeData);
dayjs.extend(weekOfYear);

const { RangePicker } = DatePicker;

const pad2 = (number) => String(number).padStart(2, '0');

const startOfDay = (date) =>
    new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);

const endOfDay = (date) =>
    new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);

const startOfWeekMon = (date) => {
    const shiftedDate = new Date(date);
    const dayOfWeek = shiftedDate.getDay();
    const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    shiftedDate.setDate(shiftedDate.getDate() + diff);
    return startOfDay(shiftedDate);
};

const startOfMonth = (date) =>
    new Date(date.getFullYear(), date.getMonth(), 1, 0, 0, 0, 0);

const fmtDay = (date) =>
    `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;

const isSameDateRange = (rangeA, rangeB) => {
    if (!rangeA || !rangeB) return false;
    const [rangeAStart, rangeAEnd] = rangeA;
    const [rangeBStart, rangeBEnd] = rangeB;
    if (!rangeAStart || !rangeAEnd || !rangeBStart || !rangeBEnd) return false;
    return (
        startOfDay(rangeAStart).getTime() === startOfDay(rangeBStart).getTime() &&
        endOfDay(rangeAEnd).getTime() === endOfDay(rangeBEnd).getTime()
    );
};

const PRESET_LABELS = {
    week_to_date: 'Week to date',
    month_to_date: 'Month to date',
    last_7_days: 'Last 7 days',
    last_14_days: 'Last 14 days',
    last_30_days: 'Last 30 days',
};

export default function GlobalTimeFilter({ onRangeChange }) {
    const [datePreset, setDatePreset] = useState('month_to_date');
    const [customRange, setCustomRange] = useState(null);
    const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);

    const effectiveRange = useMemo(() => {
        if (datePreset === 'custom') return customRange || [null, null];

        const now = new Date();
        if (datePreset === 'week_to_date') return [startOfWeekMon(now), endOfDay(now)];
        if (datePreset === 'month_to_date') return [startOfMonth(now), endOfDay(now)];
        if (datePreset === 'last_7_days') {
            const from = new Date(now);
            from.setDate(from.getDate() - 6);
            return [startOfDay(from), endOfDay(now)];
        }
        if (datePreset === 'last_14_days') {
            const from = new Date(now);
            from.setDate(from.getDate() - 13);
            return [startOfDay(from), endOfDay(now)];
        }
        if (datePreset === 'last_30_days') {
            const from = new Date(now);
            from.setDate(from.getDate() - 29);
            return [startOfDay(from), endOfDay(now)];
        }

        return [null, null];
    }, [datePreset, customRange]);

    useEffect(() => {
        onRangeChange(effectiveRange?.[0] || null, effectiveRange?.[1] || null);
    }, [effectiveRange]);

    const rangePickerValue = useMemo(() => (
        effectiveRange?.[0] && effectiveRange?.[1]
            ? [dayjs(effectiveRange[0]), dayjs(effectiveRange[1])]
            : null
    ), [effectiveRange]);

    const dateDisplayText = useMemo(() => {
        if (datePreset !== 'custom' && PRESET_LABELS[datePreset]) {
            return PRESET_LABELS[datePreset];
        }

        const [from, to] = effectiveRange || [];
        if (!from || !to) return 'All dates';
        if (startOfDay(from).getTime() === startOfDay(to).getTime()) return fmtDay(from);
        return `${fmtDay(from)} to ${fmtDay(to)}`;
    }, [datePreset, effectiveRange]);

    return (
        <div className='relative'>
            <div
                className='h-9 rounded-lg border border-landing-gray-300 hover:border-backgrounds-primary300 transition-colors bg-backgrounds-white px-4 flex items-center text-sm cursor-pointer'
                onClick={() => setIsDatePickerOpen(true)}
            >
                {dateDisplayText}
            </div>

            <RangePicker
                open={isDatePickerOpen}
                onOpenChange={setIsDatePickerOpen}
                style={{ width: '100%', opacity: 0, position: 'absolute', inset: 0, pointerEvents: 'none' }}
                allowClear={false}
                value={rangePickerValue}
                disabledDate={(current) => current && current > dayjs().endOf('day')}
                maxDate={dayjs().endOf('day')}
                presets={[
                    {
                        label: 'Week to date',
                        value: [dayjs(startOfWeekMon(new Date())), dayjs(endOfDay(new Date()))],
                    },
                    {
                        label: 'Month to date',
                        value: [dayjs(startOfMonth(new Date())), dayjs(endOfDay(new Date()))],
                    },
                    {
                        label: 'Last 7 days',
                        value: [dayjs(startOfDay(dayjs().subtract(6, 'day').toDate())), dayjs(endOfDay(new Date()))],
                    },
                    {
                        label: 'Last 14 days',
                        value: [dayjs(startOfDay(dayjs().subtract(13, 'day').toDate())), dayjs(endOfDay(new Date()))],
                    },
                    {
                        label: 'Last 30 days',
                        value: [dayjs(startOfDay(dayjs().subtract(29, 'day').toDate())), dayjs(endOfDay(new Date()))],
                    },
                ]}
                onChange={(vals) => {
                    if (!vals || !vals[0] || !vals[1]) return;

                    const chosen = [startOfDay(vals[0].toDate()), endOfDay(vals[1].toDate())];
                    const now = new Date();
                    const presets = {
                        week_to_date: [startOfWeekMon(now), endOfDay(now)],
                        month_to_date: [startOfMonth(now), endOfDay(now)],
                        last_7_days: (() => {
                            const from = new Date(now);
                            from.setDate(from.getDate() - 6);
                            return [startOfDay(from), endOfDay(now)];
                        })(),
                        last_14_days: (() => {
                            const from = new Date(now);
                            from.setDate(from.getDate() - 13);
                            return [startOfDay(from), endOfDay(now)];
                        })(),
                        last_30_days: (() => {
                            const from = new Date(now);
                            from.setDate(from.getDate() - 29);
                            return [startOfDay(from), endOfDay(now)];
                        })(),
                    };

                    const matched = Object.entries(presets).find(([, range]) =>
                        isSameDateRange(chosen, range)
                    );

                    if (matched?.[0]) {
                        setDatePreset(matched[0]);
                    } else {
                        setDatePreset('custom');
                        setCustomRange(chosen);
                    }
                }}
            />
        </div>
    );
}
